// Reporting chart date helpers — PURE (no `Date.now()` / argless `new Date()`).
//
// Two concerns, both keyed on the active grouping (daily / weekly / monthly):
//   1. bucketIdFor — which bucket a calendar day falls into (the grouping key
//      the trend shaper sums into).
//   2. formatBucketLabel — the shared X-axis / tooltip label for a bucket id:
//        daily   → "Sat 5 Jul"  (abbreviated weekday + day + month)
//        weekly  → "Wk of 29 Jun" (the week's Monday)
//        monthly → "Jul 2026"
// Dates are parsed by hand into UTC so no timezone shift moves a day.

export type TrendGrouping = 'daily' | 'weekly' | 'monthly';

export const TREND_GROUPINGS: TrendGrouping[] = ['daily', 'weekly', 'monthly'];
export const DEFAULT_TREND_GROUPING: TrendGrouping = 'daily';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad2 = (n: number): string => String(n).padStart(2, '0');

interface Ymd {
  y: number;
  m: number; // 1-12
  d: number; // 1-31
}

function parseIsoDate(iso: string): Ymd | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

// A UTC Date for a plain calendar date (no local-tz drift).
function utcDate(p: Ymd): Date {
  return new Date(Date.UTC(p.y, p.m - 1, p.d));
}

function isoOf(dt: Date): string {
  return `${dt.getUTCFullYear()}-${pad2(dt.getUTCMonth() + 1)}-${pad2(dt.getUTCDate())}`;
}

// The Monday (ISO week start) of the week containing `iso`, as 'YYYY-MM-DD'.
export function weekStartIso(iso: string): string {
  const p = parseIsoDate(iso);
  if (!p) return iso;
  const dt = utcDate(p);
  const dow = dt.getUTCDay(); // 0 Sun … 6 Sat
  const daysSinceMonday = (dow + 6) % 7;
  dt.setUTCDate(dt.getUTCDate() - daysSinceMonday);
  return isoOf(dt);
}

// The bucket key a calendar day falls into for the given grouping. daily → the
// day; weekly → the week's Monday ('YYYY-MM-DD'); monthly → 'YYYY-MM'.
export function bucketIdFor(iso: string, grouping: TrendGrouping): string {
  if (grouping === 'weekly') return weekStartIso(iso);
  if (grouping === 'monthly') {
    const p = parseIsoDate(iso);
    return p ? `${p.y}-${pad2(p.m)}` : iso;
  }
  return iso;
}

// The COMPLETE, continuous list of bucket ids across [from, to] inclusive for the
// grouping — one entry per calendar bucket, EMPTY buckets included (the domain the
// chart shaper left-joins data onto so gaps aren't collapsed). Enumerated in the
// same UTC date basis as bucketIdFor, so there's no off-by-one against the stored
// keys. Returns [] for an unparseable / inverted range.
//   daily   → every day from..to
//   weekly  → every Monday from weekStart(from) to weekStart(to)
//   monthly → every 'YYYY-MM' from from's month to to's month
export function enumerateBucketDomain(
  from: string,
  to: string,
  grouping: TrendGrouping,
): string[] {
  const start = parseIsoDate(from);
  const end = parseIsoDate(to);
  if (!start || !end) return [];
  if (utcDate(start).getTime() > utcDate(end).getTime()) return [];

  if (grouping === 'monthly') {
    const out: string[] = [];
    let y = start.y;
    let m = start.m; // 1-12
    while (y < end.y || (y === end.y && m <= end.m)) {
      out.push(`${y}-${pad2(m)}`);
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    return out;
  }

  // daily + weekly both walk a UTC cursor by a fixed day step.
  const stepDays = grouping === 'weekly' ? 7 : 1;
  const firstIso = grouping === 'weekly' ? weekStartIso(from) : from;
  const lastIso = grouping === 'weekly' ? weekStartIso(to) : to;
  const cursor = utcDate(parseIsoDate(firstIso)!);
  const lastMs = utcDate(parseIsoDate(lastIso)!).getTime();
  const out: string[] = [];
  while (cursor.getTime() <= lastMs) {
    out.push(isoOf(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + stepDays);
  }
  return out;
}

// The shared display label for a bucket id, keyed on grouping (see file header).
export function formatBucketLabel(bucket: string, grouping: TrendGrouping): string {
  if (grouping === 'monthly') {
    const m = /^(\d{4})-(\d{2})$/.exec(bucket);
    if (!m) return bucket;
    return `${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
  }
  const p = parseIsoDate(bucket);
  if (!p) return bucket;
  if (grouping === 'weekly') {
    return `Wk of ${p.d} ${MONTHS[p.m - 1]}`;
  }
  const dow = utcDate(p).getUTCDay();
  return `${WEEKDAYS[dow]} ${p.d} ${MONTHS[p.m - 1]}`;
}
